import { getDefaultSchema } from '@/business-components/request-parameter/utils/getDefaultSchema';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import type { HttpApiServerType } from '@/services/http-api/httpApi';
import { generateExampleByJsonSchema } from '@/shared/util/generateExampleByJsonSchema';
import { HttpApiRequestBodyType } from '@tarslib/types/lib/httpApi';
import React from 'react';
import { v4 as uuid } from 'uuid';

export function useGenerateDefaultExample(value: HttpApiServerType): HttpApiServerType {
  const dataSchemaDefinitions = useDocsDataModelStore(
    (x) => x.resourceData.extraData.dataSchemaDefinitions,
  );
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);

  return React.useMemo(() => {
    const requestBodyExamples = value.requestBody.examples.filter((x) => x.value);

    const newValue: HttpApiServerType = {
      ...value,
      requestBody: {
        ...value.requestBody,
        examples: (() => {
          if (requestBodyExamples.length === 0) {
            if (
              [
                HttpApiRequestBodyType.Json,
                HttpApiRequestBodyType.Xml,
                HttpApiRequestBodyType.MsgPack,
              ].includes(value.requestBody.type)
            ) {
              return [
                {
                  key: uuid(),
                  value: generateExampleByJsonSchema(
                    value.requestBody.jsonSchema ?? getDefaultSchema(),
                    dataSchemaDefinitions,
                    value.requestBody.type === HttpApiRequestBodyType.Xml,
                    'request',
                  ),
                  description: '',
                  mediaType: value.requestBody.mediaType || value.requestBody.type,
                },
              ];
            }
          }
          return requestBodyExamples;
        })().map((x, i) => {
          return {
            ...x,
            name: x.name || `${clientI18n('api.label.example')} ${i + 1}`,
          };
        }),
      },
      responses: value.responses.map((x) => {
        const newResponseExamples = [...(x.responseExamples ?? [])].filter((y) => y.data);
        if (
          newResponseExamples.length === 0 &&
          (x.contentType === 'json' || x.contentType === 'xml' || x.contentType === 'msgpack')
        ) {
          const data = generateExampleByJsonSchema(
            x.jsonSchema ?? getDefaultSchema(),
            dataSchemaDefinitions,
            x.contentType === 'xml',
            'response',
          );

          newResponseExamples.push({
            id: (x.id ?? 10) * 1000000000000,
            data,
            description: '',
            name: '',
            ordering: 1,
            responseId: x.id,
          });
        }

        return {
          ...x,
          responseExamples: newResponseExamples.map((y, i) => {
            return {
              ...y,
              name: y.name || `${clientI18n('api.label.example')} ${i + 1}`,
            };
          }),
        };
      }),
    };
    return newValue;
  }, [value, dataSchemaDefinitions, clientI18n]);
}
